import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Nav, Tab } from 'react-bootstrap';

import { CreateUpdateCompanyDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  PersonalLanguageType,
  PersonalLanguageTypeObj,
  TemplateTypeEnum,
  TemplateTypeObj,
} from '../../../common/pokCore/validation/enums';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { CompanyBasicDataTab } from './CompanyBasicDataTab';
import { CompanyVisualDataTab } from './CompanyVisualDataTab';
import { CompanySensitiveDataTab } from './CompanySensitiveDataTab';

interface CompanyEditorFormProps {
  id?: string;
  company: CreateUpdateCompanyDto;
  show?: boolean;
  creatingCompany?: boolean;
  propertyChange?: (obj: Partial<CreateUpdateCompanyDto>) => void;
}

const MIN_PROJECT_DATE = '2026-02-01';

export const CompanyEditorForm: React.FC<CompanyEditorFormProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const propertyChange = (obj: Partial<CreateUpdateCompanyDto>) => {
    if (props.propertyChange) {
      props.propertyChange(obj);
    }
  };

  const setFile = (logo: string) => {
    propertyChange({ logo: logo });
  };

  const getAllPersonalLanguages = useCallback(() => {
    const arr = Object.keys(PersonalLanguageTypeObj).map(name => {
      return {
        value: PersonalLanguageType[name as keyof typeof PersonalLanguageType],
        label: t(
          PersonalLanguageTypeObj[name as keyof typeof PersonalLanguageTypeObj],
        ),
      };
    });
    return arr;
  }, [t]);

  const templateTypeOptions = useMemo(() => {
    return Object.entries(TemplateTypeEnum).map(([key, value]) => ({
      value: key as TemplateTypeEnum,
      label: t(TemplateTypeObj[value]),
    }));
  }, [t]);

  const getSelectedLanguage = useCallback(() => {
    return getAllPersonalLanguages().filter(
      item => item.value === props.company.personalLanguage,
    );
  }, [props.company.personalLanguage, getAllPersonalLanguages]);

  const [selectedLanguage, setSelectedLanguage] = useState(
    getSelectedLanguage(),
  );

  useEffect(() => {
    const lang = getSelectedLanguage();
    setSelectedLanguage(lang);
  }, [props.company.personalLanguage, getSelectedLanguage]);

  return (
    <Tab.Container defaultActiveKey="basic" id="company-tabs">
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="basic">{t(tk.administration.basicData)}</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="visual">
            {t(tk.administration.visualData)}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="sensitive">
            {t(tk.administration.sensitiveData)}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        <Tab.Pane eventKey="basic">
          <CompanyBasicDataTab
            company={props.company}
            propertyChange={propertyChange}
            personalLanguageOptions={getAllPersonalLanguages()}
            selectedLanguage={selectedLanguage}
            templateTypeOptions={templateTypeOptions}
            setFile={setFile}
          />
        </Tab.Pane>

        <Tab.Pane eventKey="visual">
          <CompanyVisualDataTab
            company={props.company}
            propertyChange={propertyChange}
            setFile={setFile}
          />
        </Tab.Pane>

        <Tab.Pane eventKey="sensitive">
          <CompanySensitiveDataTab
            company={props.company}
            propertyChange={propertyChange}
            minProjectDate={MIN_PROJECT_DATE}
          />
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );
};
